<template>
  <div class="dva-container">
      <!-- Back Header -->
      <div class="dva-header">
        <div class="dva-back-btn" @click="$emit('close')">
          <i class="fa fa-arrow-left"></i>
          <span>Demo Video</span>
        </div>
        <p class="dva-subtitle">Level wise analysis of all the questions</p>
      </div>

      <div v-if="loading" class="dva-loading-state">
        <i class="fa fa-spinner fa-spin fa-3x" style="color: #3b82f6; margin-bottom: 12px;"></i>
        <p style="margin: 0; font-size: 14px; color: #94a3b8;">Loading video analysis...</p>
      </div>

      <!-- Cards Grid -->
      <div v-else class="dva-body">
        <div class="dva-grid">
          <div v-for="(slide, idx) in displaySlides" :key="slide.id || idx" class="gt-dashboard-card gt-cpa-card-white">
            <!-- Card Header -->
            <div class="gt-dashboard-card-header gt-cpa-card-header-white">
              <span class="gt-cpa-card-title">Question {{ idx + 1 }}</span>
              <div class="gt-cpa-header-stats">
                <span class="gt-stat-attempted">{{ getPercentAttempted(slide) }}% Attempted</span>
                <span class="gt-stat-stuck">{{ getPercentStuck(slide) }}% Stuck</span>
                <span class="gt-stat-wrong">{{ getPercentWrong(slide) }}% Wrong</span>
              </div>
            </div>

            <!-- Card Body -->
            <div class="gt-dashboard-card-body">
              <!-- Image if normal slide (Non-CPA) -->
              <template v-if="!isCpaQuestion(slide)">
                <div style="padding: 16px; background: #ffffff; text-align: center; flex: 1; display: flex; flex-direction: column; justify-content: center;">
                  <div
                    v-if="slide.pic"
                    class="gt-slide-image"
                    :style="{
                      backgroundImage: 'url(' + Fn.hashToUrl(slide.pic) + ')',
                      minHeight: '180px',
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      background: '#f1f5f9',
                      borderRadius: '8px'
                    }"></div>
                  <div v-else style="min-height: 180px; display: flex; justify-content: center; align-items: center; color: #94a3b8; background: #f1f5f9; border-radius: 8px;">
                    <i class="fa fa-image" style="font-size: 32px;"></i>
                  </div>
                </div>
                <div style="padding: 16px 20px; display: flex; justify-content: flex-end; align-items: center; gap: 8px; border-top: 1px solid #f1f5f9;">
                  <button class="gt-cpa-view-btn" @click.stop="handlePreviewImage(slide)">
                    <i class="fa fa-eye" style="margin-right: 4px;"></i> VIEW
                  </button>
                  <template v-if="hasPostGameDemo(slide)">
                    <button
                      class="gt-cpa-view-btn"
                      style="color: #64748b; border-color: #cbd5e1;"
                      @click.stop="handleRedoDemo(null, slide)">
                      RE-DO DEMO
                    </button>
                  </template>
                  <template v-else>
                    <button
                      class="gt-cpa-view-btn"
                      style="color: #1e293b; border-color: #94a3b8;"
                      @click.stop="handleShowDemo(null, slide)">
                      SHOW DEMO
                    </button>
                  </template>
                  <i v-if="hasPostGameDemo(slide)" class="fa fa-check-circle" style="color: #10b981; font-size: 14px"></i>
                </div>
              </template>

              <!-- CPA Question Details (Bloom Table) -->
              <template v-else>
                <div style="position: relative; flex: 1; min-height: 0; display: flex; flex-direction: column">
                  <div class="gt-bloom-table-container gt-cpa-table-container">
                    <div class="gt-bloom-table gt-cpa-table">
                      <div class="gt-bloom-header gt-cpa-table-header" style="position: relative">
                        <div
                          style="
                            flex: 2.5;
                            min-width: 135px;
                            padding-left: 12px;
                            padding-right: 4px;
                            position: sticky;
                            left: 0;
                            background: #ffffff;
                            z-index: 2;
                          ">
                          BLOOM LEVEL
                        </div>
                        <div style="flex: 1.5; min-width: 80px; text-align: center">ATTEMPTED</div>
                        <div style="flex: 1.5; min-width: 80px; text-align: center">STUCK</div>
                        <div style="flex: 1.5; min-width: 80px; text-align: center">WRONG</div>
                        <div style="flex: 2.5; min-width: 120px"></div>
                      </div>
                      <div class="gt-bloom-rows gt-cpa-rows">
                        <div v-for="(stat, sIdx) in getCpaBloomStats(slide)" :key="sIdx" class="gt-bloom-row gt-cpa-row" style="position: relative">
                          <div
                            class="gt-cpa-col-level"
                            style="
                              flex: 2.5;
                              min-width: 135px;
                              padding-left: 12px;
                              padding-right: 4px;
                              position: sticky;
                              left: 0;
                              background: #f8fafc;
                              z-index: 2;
                              display: flex;
                              align-items: center;
                              gap: 6px;
                            ">
                            <div class="gt-cpa-level-badge">{{ stat.levelNum }}</div>
                            <div class="gt-cpa-level-name" style="width: 68px; flex: none">{{ stat.levelNameFormated }}</div>
                          </div>
                          <div class="gt-cpa-col-stat" style="flex: 1.5; min-width: 80px; color: #1e293b; font-weight: bold; text-align: center">
                            {{ stat.attempt }}/{{ attendedStudentCount }}
                          </div>
                          <div class="gt-cpa-col-stat" style="flex: 1.5; min-width: 80px; color: #eab308; font-weight: bold; text-align: center">
                            {{ stat.stuck }}/{{ attendedStudentCount }}
                          </div>
                          <div class="gt-cpa-col-stat" style="flex: 1.5; min-width: 80px; color: #ef4444; font-weight: bold; text-align: center">
                            {{ stat.wrong }}/{{ attendedStudentCount }}
                          </div>
                          <div
                            style="
                              flex: 2.5;
                              min-width: 120px;
                              text-align: right;
                              padding-right: 12px;
                              display: flex;
                              justify-content: flex-end;
                              align-items: center;
                              gap: 8px;
                            ">
                            <template v-if="stat.hasDemo">
                              <button
                                class="gt-cpa-view-btn"
                                style="color: #64748b; border-color: #cbd5e1;"
                                @click.stop="handleRedoDemo(stat, slide)">
                                RE-DO DEMO
                              </button>
                            </template>
                            <template v-else>
                              <button
                                class="gt-cpa-view-btn"
                                style="color: #1e293b; border-color: #94a3b8;"
                                @click.stop="handleShowDemo(stat, slide)">
                                SHOW DEMO
                              </button>
                            </template>
                            <i v-if="stat.hasDemo" class="fa fa-check-circle" style="color: #10b981; font-size: 14px"></i>
                            <div v-else style="width: 16px"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Mask to cover horizontal scrollbar under fixed BLOOM column -->
                  <div
                    style="position: absolute; left: 0; bottom: 0; width: 135px; height: 6px; background: #ffffff; z-index: 5; pointer-events: none"></div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

<script>
export default {
  name: 'DemoVideoAnalysis',
  props: {
    slides: {type: Array, default: () => []},
    responses: {type: Array, default: () => []},
    analysis: {type: Object, default: () => null},
    gameOnRecord: {type: Object, default: () => null},
    activityLogs: {type: Array, default: () => []},
    existingDemoQuestionIds: {type: Array, default: () => []},
    loading: {type: Boolean, default: false},
  },
  computed: {
    Fn() {
      return window.Fn || {}
    },
    displaySlides() {
      if (this.slides && this.slides.length) {
        return this.slides
      }
      return []
    },
    attendedStudentCount() {
      const record = this.gameOnRecord
      if (record && record.members) {
        const membersCount = record.members.length + (record.guestMembers?.length || 0)
        if (membersCount > 0) return membersCount
      }
      if (this.responses && this.responses.length) {
        const studentIds = new Set(this.responses.map(r => r.studentId || r.userId || r.peer || r.name).filter(Boolean));
        return studentIds.size || 30;
      }
      return 30;
    }
  },
  methods: {
    isCpaQuestion(slide) {
      if (slide.hasOwnProperty('isCpa')) {
        return slide.isCpa
      }
      const fullQuestion = this.getFullQuestion(slide)
      return !!(fullQuestion && fullQuestion.cpa && fullQuestion.cpa.outcome)
    },
    getFullQuestion(slide) {
      if (!slide) return null
      const pageId = slide.page || slide.id || slide.pageId
      const session = this.$store && this.$store.state && this.$store.state.session
      const questions = session && session.questions ? session.questions : []
      return questions.find((q) => q.page === pageId || q.id === pageId) || slide
    },
    getPercentAttempted(slide) {
      const record = this.gameOnRecord
      const members = (record && record.members) || []
      const guestMembers = (record && record.guestMembers) || []
      const allMembers = [...members, ...guestMembers]
      if (allMembers.length === 0) return 0

      const pageId = slide.pageId || slide.page || slide.id
      let attemptedCount = 0
      allMembers.forEach((m) => {
        const isAttempted =
          (m.lockedPages && m.lockedPages.includes(pageId)) ||
          (m.lockedQuestionIds && m.lockedQuestionIds.includes(pageId))
        if (isAttempted) attemptedCount++
      })
      return Math.round((attemptedCount / allMembers.length) * 100)
    },
    getPercentStuck(slide) {
      const record = this.gameOnRecord
      const members = (record && record.members) || []
      const guestMembers = (record && record.guestMembers) || []
      const allMembers = [...members, ...guestMembers]
      if (allMembers.length === 0) return 0

      const pageId = slide.pageId || slide.page || slide.id
      let stuckCount = 0
      allMembers.forEach((m) => {
        const hasStuckLog = this.activityLogs && this.activityLogs.some((l) => l.pageId === pageId && l.uid === m.uid && l.isStuck)
        const hasCorrectAnswer = (m.correctPages && m.correctPages.includes(pageId)) || (m.correctQuestionIds && m.correctQuestionIds.includes(pageId))
        if (hasStuckLog && !hasCorrectAnswer) stuckCount++
      })
      return Math.round((stuckCount / allMembers.length) * 100)
    },
    getPercentCorrect(slide) {
      const record = this.gameOnRecord
      const members = (record && record.members) || []
      const guestMembers = (record && record.guestMembers) || []
      const allMembers = [...members, ...guestMembers]
      if (allMembers.length === 0) return 0

      const pageId = slide.pageId || slide.page || slide.id
      let correctHits = 0
      allMembers.forEach((m) => {
        const isCorrect =
          (m.correctPages && m.correctPages.includes(pageId)) ||
          (m.correctQuestionIds && m.correctQuestionIds.includes(pageId))
        if (isCorrect) correctHits++
      })
      return Math.round((correctHits / allMembers.length) * 100)
    },
    getPercentWrong(slide) {
      const attempted = this.getPercentAttempted(slide)
      const correct = this.getPercentCorrect(slide)
      return Math.max(0, attempted - correct)
    },
    hasPostGameDemo(slide, questionId = null) {
      if (!slide) return false

      const targetId = questionId || slide.pageId || slide.page || slide.id
      if (!targetId) return false

      // Check in the list of existing DB recordings passed from parent
      if (this.existingDemoQuestionIds && this.existingDemoQuestionIds.some((id) => String(id) === String(targetId))) {
        return true
      }

      if (this.gameOnRecord && this.gameOnRecord.postGameDemos) {
        return this.gameOnRecord.postGameDemos.some((id) => String(id) === String(targetId))
      }

      return false
    },
    getCpaBloomStats(slide) {
      const fullQuestion = this.getFullQuestion(slide)
      const BloomLabels = ['REMEMBER', 'UNDERSTAND', 'APPLY', 'ANALYZE', 'EVALUATE', 'CREATE']

      let questionsToProcess = []

      if (fullQuestion && fullQuestion.bloom_questions) {
        Object.entries(fullQuestion.bloom_questions).forEach(([key, qId]) => {
          questionsToProcess.push({
            id: qId,
            bloomValue: parseInt(key) + 1,
          })
        })
      } else {
        questionsToProcess.push({
          id: fullQuestion ? fullQuestion._id || fullQuestion.id || slide.page : slide.page,
          bloomValue: fullQuestion && fullQuestion.bloom && fullQuestion.bloom.length > 0 ? fullQuestion.bloom[0] : 4,
        })
      }

      const record = this.gameOnRecord
      const members = (record && record.members) || []
      const guestMembers = (record && record.guestMembers) || []
      const allMembers = [...members, ...guestMembers]

      return questionsToProcess.map((q) => {
        let attemptCount = 0
        let correctHits = 0
        let stuckCount = 0

        allMembers.forEach((m) => {
          const isRootQuestion = fullQuestion && (q.id === fullQuestion._id || q.id === fullQuestion.id)

          const isAttempted =
            (m.lockedPages && m.lockedPages.includes(q.id)) ||
            (isRootQuestion && m.lockedPages && m.lockedPages.some((pId) => pId === slide.page || pId === slide.id)) ||
            (m.lockedQuestionIds && m.lockedQuestionIds.includes(q.id))

          const isCorrect =
            (m.correctPages && m.correctPages.includes(q.id)) ||
            (isRootQuestion && m.correctPages && m.correctPages.some((pId) => pId === slide.page || pId === slide.id)) ||
            (m.correctQuestionIds && m.correctQuestionIds.includes(q.id))

          const hasStuckLog =
            this.activityLogs &&
            this.activityLogs.some(
              (l) => (l.pageId === q.id || l.questionId === q.id || (isRootQuestion && (l.pageId === slide.page || l.pageId === slide.id))) && l.uid === m.uid && l.isStuck
            )
          const isStuck = hasStuckLog && !isCorrect

          if (isAttempted) {
            attemptCount++
          }
          if (isCorrect) {
            correctHits++
          }
          if (isStuck) {
            stuckCount++
          }
        })

        let wrongHits = attemptCount - correctHits
        let bloomValue = q.bloomValue
        let levelName = BloomLabels[bloomValue - 1] || 'ANALYZE'
        let levelPrefix = `LEVEL ${bloomValue} : `

        const hasDemo = this.hasPostGameDemo(slide, q.id)

        return {
          questionId: q.id,
          levelPrefix: levelPrefix,
          levelName: levelName,
          levelNum: bloomValue,
          levelNameFormated: levelName.charAt(0).toUpperCase() + levelName.slice(1).toLowerCase(),
          attempt: attemptCount,
          correct: correctHits,
          stuck: stuckCount,
          wrong: wrongHits,
          hasDemo: !!hasDemo,
        }
      })
    },
    handleShowDemo(stat, slide, forceRecord = false) {
      this.$emit('show-demo', {slide, stat, forceRecord})
    },
    handleRedoDemo(stat, slide) {
      this.handleShowDemo(stat, slide, true)
    },
    handlePreviewImage(slide) {
      if (slide && slide.pic) {
        const hashToUrl = (window.Fn && window.Fn.hashToUrl) || ((h) => h)
        window.open(hashToUrl(slide.pic), '_blank')
      }
    }
  }
}
</script>

<style scoped>
.dva-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.dva-header {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 24px 20px 24px;
}

.dva-back-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  color: #0f172a;
  font-size: 24px;
  font-weight: 600;
  user-select: none;
}

.dva-back-btn span {
  font-size: inherit !important;
  font-weight: inherit !important;
}

.dva-back-btn i {
  font-size: 24px !important;
  transition: transform 0.2s;
}

.dva-back-btn:hover i {
  transform: translateX(-4px);
}

.dva-subtitle {
  margin: 2px 0 0 36px;
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
}

.dva-body {
  width: 100%;
  flex: 1;
  overflow-y: auto;
  padding: 16px 0 32px 0;
}

.dva-grid {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

/* gt-dashboard-card styling copied from GameTrackingPage.vue */
.gt-dashboard-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: auto; /* auto height so cards expand perfectly */
}

.gt-dashboard-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.gt-cpa-card-title {
  font-weight: bold;
  font-size: 13px;
  color: #334155;
}

.gt-cpa-header-stats {
  display: flex;
  gap: 12px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.gt-cpa-header-stats span {
  font-size: inherit !important;
  font-weight: inherit !important;
}

.gt-non-cpa-stats span {
  font-size: inherit !important;
  font-weight: inherit !important;
}

.gt-stat-attempted {
  color: #64748b;
}

.gt-stat-stuck {
  color: #eab308;
}

.gt-stat-wrong {
  color: #ef4444;
}

.gt-dashboard-card-body {
  padding: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* gt-bloom-table styling */
.gt-bloom-table-container {
  width: 100%;
  overflow-x: auto;
}

.gt-bloom-table {
  width: 100%;
  min-width: 420px;
  border-collapse: collapse;
}

.gt-bloom-header {
  display: flex;
  padding: 12px 20px;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

.gt-bloom-row {
  display: flex;
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
}

.gt-bloom-row:last-child {
  border-bottom: none;
}

.gt-cpa-table-header {
  padding: 16px 20px 8px 20px !important;
}

.gt-cpa-table-header > div {
  color: #64748b !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  letter-spacing: 0.5px !important;
}

.gt-cpa-rows {
  gap: 10px !important;
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px 20px !important;
}

.gt-cpa-row {
  background: #f8fafc !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 12px 0 !important;
  display: flex;
  align-items: center;
  width: 100%;
}

.gt-cpa-col-level {
  display: flex;
  align-items: center;
  gap: 8px;
  border-top-left-radius: 8px !important;
  border-bottom-left-radius: 8px !important;
}

.gt-cpa-level-badge {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #64748b;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
}

.gt-cpa-level-name {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

.gt-cpa-col-stat {
  font-size: 12px !important;
  font-weight: 600 !important;
  text-align: center;
}

.gt-cpa-view-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  color: #475569;
  font-weight: 600;
  font-size: 11px;
  padding: 6px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  letter-spacing: 0.5px;
}

.gt-cpa-view-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #1e293b;
}

.dva-done-badge {
  background: #d1fae5;
  color: #065f46;
  font-size: 10px;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  letter-spacing: 0.5px;
}

.text-success-icon {
  color: #10b981 !important;
}

/* Responsive CSS */
@media (max-width: 992px) {
  .dva-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dva-header {
    padding: 20px 16px 12px 16px;
  }
  
  .dva-back-btn {
    font-size: 22px;
  }
  
  .dva-back-btn i {
    font-size: 20px;
  }
  
  .dva-subtitle {
    margin-left: 32px;
    font-size: 12px;
  }
  
  .dva-body {
    padding: 8px 0 20px 0;
  }
  
  .dva-grid {
    padding: 0 16px;
    gap: 16px;
  }
  
  .dva-table-header {
    padding: 10px 12px;
  }
  
  .dva-table-row {
    padding: 10px 12px;
  }
  
  .dva-level-badge {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }
  
  .dva-level-name {
    font-size: 12px;
  }
  
  .dva-col-stat {
    font-size: 12px;
  }
  
  .dva-done-badge {
    font-size: 9px;
    padding: 4px 8px;
  }
  
  .gt-cpa-view-btn {
    font-size: 10px;
    padding: 4px 10px;
  }
}

.dva-loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
}
</style>
